import { customerParsers } from "stores/customer/customerParsers";

/**
 * Decodes PHC onboarding preview URL `token` query param.
 * Supports JWT-shaped payloads (middle segment) or a single base64url JSON blob.
 */
function decodeBase64Url(segment) {
  if (!segment) return "";
  const pad =
    segment.length % 4 === 0 ? "" : "=".repeat(4 - (segment.length % 4));
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/") + pad;
  return decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
}

function normalizeAuthPayload(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  const token = parsed.token || parsed.accessToken;
  if (!token) return null;
  return {
    token,
    legacyToken:
      parsed.legacyToken ??
      parsed.phpToken ??
      parsed.customers?.phpToken ??
      null,
  };
}

function authFromLoginShapedPayload(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  const viaParser = customerParsers.login(parsed);
  if (!viaParser?.token) return null;
  return {
    token: viaParser.token,
    legacyToken: viaParser.legacyToken,
  };
}

function extractAuthFromJwtString(jwtStr) {
  const parts = jwtStr.split(".");
  if (parts.length !== 3) return null;
  const json = decodeBase64Url(parts[1]);
  const parsed = JSON.parse(json);
  const flat =
    normalizeAuthPayload(parsed) || authFromLoginShapedPayload(parsed);
  if (flat) return flat;
  // Preview-style JWT: claims identify the user; the JWT itself is the bearer token
  if (parsed && (parsed.customerId != null || parsed.jwtid)) {
    return {
      token: jwtStr,
      legacyToken:
        parsed.legacyToken ?? parsed.phpToken ?? parsed.customers?.phpToken ?? null,
    };
  }
  return null;
}

function extractAuthFromJsonString(jsonStr) {
  const parsed = JSON.parse(jsonStr);
  return (
    normalizeAuthPayload(parsed) || authFromLoginShapedPayload(parsed)
  );
}

export function decodePhcPreviewAuthToken(encoded) {
  if (!encoded || typeof encoded !== "string") return null;
  const trimmed = encoded.trim();
  try {
    const directJwt = extractAuthFromJwtString(trimmed);
    if (directJwt) return directJwt;

    const inner = decodeBase64Url(trimmed);
    if (!inner) return null;

    const innerTrim = inner.trim();
    const nestedJwt = extractAuthFromJwtString(innerTrim);
    if (nestedJwt) return nestedJwt;

    return extractAuthFromJsonString(innerTrim);
  } catch (e) {
    console.warn("decodePhcPreviewAuthToken: invalid token", e);
    return null;
  }
}

/** True when URL is PHC onboarding preview (e.g. /phc/onboarding/preview). */
export function isPhcOnboardingPreviewPath(pathname) {
  return typeof pathname === "string" && pathname.includes("/onboarding/preview");
}

export function getPhcPreviewAuthFromLocation(pathname, search) {
  if (!isPhcOnboardingPreviewPath(pathname)) return null;
  const raw = new URLSearchParams(search || "").get("token");
  if (!raw) return null;
  return decodePhcPreviewAuthToken(raw);
}

/** Lets unauthenticated users reach /phc/* only when preview link has a decodable token. */
export function allowsPhcEntryWithoutSession(pathname, search) {
  return Boolean(getPhcPreviewAuthFromLocation(pathname, search)?.token);
}

export function isPhcPreviewWithAuthToken(pathname, search) {
  if (!isPhcOnboardingPreviewPath(pathname)) return false;
  const params = new URLSearchParams(search || "");
  return Boolean(params.get("token"));
}
